// Memberships (M8, technical design §6.8): one office place for the members, the visits their plans
// include that are coming due, the renewals coming up, and the plans themselves. A side list of the
// parts, as Books has, over the existing actions: memberships.list and memberships.get (a member's
// detail), memberships.sell (SellMembership.tsx), recurringServices.due (booked in To book,
// memberships.bookVisit), memberships.expire (the plans whose term is over), membershipTypes.list,
// membershipTypes.create and membershipTypes.update (EditPlan.tsx); a member's suspend, resume and
// cancel, and skipping a due visit (MemberStatus.tsx, BL-056); one billed by instalments says how and
// when it bills, and its next bill is moved or its billing paused (Billing.tsx, BL-009); a bill that
// wasn't paid after its tries is at risk beside the renewals. One from ServiceTitan says so while the
// company mirrors it, and offers none of those (history fix 2). The member lists read a page at a time
// with "Load more" (member-pages.ts): a company's members run to thousands.

import { useEffect, useRef, useState } from 'react';
import { BOOKER_PERMS, Header, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { useDesign } from '../blueprint/design.ts';
import { Moved } from '../blueprint/Moved.tsx';
import { dayText, todayIn } from '../books/model.ts';
import { Empty, Loading, Money, Panel, errorText, useAct, useQuery, useToast } from '../books/ui.tsx';
import { openSellMembership } from './SellMembership.tsx';
import { MembershipsPage } from './MembershipsPage.tsx';
import { EditPlan } from './EditPlan.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { AutoRenew } from './AutoRenew.tsx';
import type { EditablePlan } from './plan-edit-model.ts';
import { onMembershipSold } from './sold.ts';
import { MemberStatusActions, SkipVisit, StatusSaid } from './MemberStatus.tsx';
import { BillingActions, BillingValue } from './Billing.tsx';
import { keepsCards, type Card } from '../history/cards-model.ts';
import { client } from '../api.ts';
import { newId } from '../shared/ids.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { useDocFocus } from '../shared/doc-link.ts';
import {
  addDaysTo,
  BILL_AT_RISK_NOTE,
  BILL_WORDS,
  discountText,
  DUE_DAYS,
  EXPIRE_PERMS,
  expiredText,
  MEMBERSHIP_SELL_PERMS,
  PLAN_PERMS,
  RENEWAL_DAYS,
  RENEWAL_HINT,
  RENEWAL_WORDS,
  renewalValue,
  sinceText,
  termSpanText,
  atRiskCount,
  CANCEL_REFUND_WORDS,
  STATUS_PERMS,
  statusWords,
  suspendNote,
  type CancelRefund,
  NOT_SET,
  termText,
  visitsText,
  whenText,
  memberFacts,
  membersInput,
  renewingInput,
  AT_RISK_INPUT,
  shownText,
  everyPage,
  type Member,
  type MemberPage,
  type MemberShow,
  type MembershipDetail,
} from './model.ts';
import { useMemberPages, useReadTo, type MemberPages } from './member-pages.ts';
import '../books/books.css';
import '../shared/office-lists.css';
import '../review/review.css';
import './sell.css';
import '../history/cards.css';
import '../history/bp-customers.css';

type Part = 'members' | 'due' | 'renewals' | 'plans';

/** Visits due's line for those who don't book: the list, without sending them to To book. */
const DUE_LEDE_READ = `Included visits coming due in the next ${DUE_DAYS} days. The office books them from To book.`;

const PARTS: { part: Part; label: string; title: string; lede: string }[] = [
  {
    part: 'members',
    label: 'Members',
    title: 'Members',
    lede: 'Everyone on a plan: what it includes, the visits used, and when it renews.',
  },
  {
    part: 'due',
    label: 'Visits due',
    title: 'Visits due',
    lede: `Included visits coming due in the next ${DUE_DAYS} days. Book them from To book.`,
  },
  {
    part: 'renewals',
    label: 'Renewals',
    title: 'Renewals',
    lede: `Renewals and bills that didn’t go through, to call first, then the plans that end in the next ${RENEWAL_DAYS} days.`,
  },
  {
    part: 'plans',
    label: 'Plans',
    title: 'Plans',
    lede: 'What each plan costs, how long it runs, the visits it includes and its member discount.',
  },
];

interface Props {
  today: string;
  toast: (m: string) => void;
  held: readonly string[];
  /** A notification's membership (BL-009): its row in view, on Renewals. */
  focus?: string;
}

function StatusChip({ m }: { m: Pick<Member, 'status' | 'cancelsOn'> }) {
  const [word, tone] = statusWords(m, dayText);
  return <span className={`chip say ${tone}`}>{word}</span>;
}

/** Where this term's renewal stands, when one has started (BL-009). */
function RenewalChip({ m, note = false }: { m: Pick<Member, 'renewal'>; note?: boolean }) {
  if (!m.renewal) return null;
  const [word, tone] = RENEWAL_WORDS[m.renewal.state];
  return (
    <>
      <span className={`chip say ${tone}`}>{word}</span>
      {note && m.renewal.note && <span className="muted small block">{m.renewal.note}</span>}
      {note && RENEWAL_HINT[m.renewal.state] && (
        <span className="muted small block">{RENEWAL_HINT[m.renewal.state]}</span>
      )}
    </>
  );
}

/** A bill of a membership billed by instalments that wasn't paid after its tries (BL-009). */
function BillChip({ m, note = false }: { m: Pick<Member, 'billing'>; note?: boolean }) {
  if (!m.billing?.atRisk) return null;
  const [word, tone] = BILL_WORDS.at_risk;
  return (
    <>
      <span className={`chip say ${tone}`}>{word}</span>
      {note && <span className="muted small block">{BILL_AT_RISK_NOTE}</span>}
    </>
  );
}

/** "2 visits: Furnace tune-up" or "No visits": what a plan's term includes. */
const includesText = (p: { visitsPerTerm: number | null; visitJobTypeName: string | null }) =>
  p.visitsPerTerm === null
    ? NOT_SET
    : p.visitsPerTerm === 0
      ? 'No visits'
      : `${p.visitsPerTerm} ${p.visitsPerTerm === 1 ? 'visit' : 'visits'}${p.visitJobTypeName ? `: ${p.visitJobTypeName}` : ''}`;

function Who({ m }: { m: Pick<Member, 'customerName' | 'address'> }) {
  return (
    <>
      <b>{m.customerName}</b>
      <span className="muted small block">{m.address}</span>
    </>
  );
}

/** Under a list read a page at a time: how many of how many, and the next page, while there is one. */
function MoreMembers({ list }: { list: MemberPages }) {
  if (!list.members || !list.first) return null;
  return (
    <>
      {list.error && (
        <p className="error" role="alert">
          {list.error}
        </p>
      )}
      {(list.more || list.busy) && (
        <div className="row member-more">
          <span className="muted small">{shownText(list.members.length, list.first.total)}</span>
          <button type="button" className="btn quiet" disabled={list.busy} onClick={() => list.more?.()}>
            {list.busy ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </>
  );
}

function Members({ today, held }: Props) {
  const bp = useDesign() === 'blueprint';
  const [status, setStatus] = useState<MemberShow>('active');
  // a name or street, looked for by the server once typing stops: the member may not be on a page in hand
  const [find, setFind] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(find.trim()), 250);
    return () => clearTimeout(t);
  }, [find]);
  // at risk: a renewal that didn't go through, whatever the membership's own status (BL-009)
  const q = useMemberPages(membersInput(status, query));
  const [open, setOpen] = useState<Member | null>(null);
  // one sold from here (or anywhere) shows at once (QA-531)
  const reload = q.reload;
  useEffect(() => onMembershipSold(() => void reload()), [reload]);
  return (
    <Panel
      title="Everyone on a plan"
      aside={
        <div className="row">
          {holdsAny(held, MEMBERSHIP_SELL_PERMS) && (
            <button className="btn" onClick={() => openSellMembership()}>
              Sell a membership
            </button>
          )}
          <label className="inline-label">
            <span className="flabel">Find</span>
            <input
              className="field"
              type="search"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder="Name or street"
            />
          </label>
          <label className="inline-label">
            <span className="flabel">Show</span>
            <select
              className="field"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="active">Current</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Ended</option>
              <option value="at_risk">Renewal at risk</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>
      }
    >
      {!q.members || !q.first ? (
        <Loading
          error={q.error}
          what="the members"
          kind="table"
          cols={['Member', 'Plan', 'Renews', 'Visits']}
          retry={() => void q.reload()}
        />
      ) : q.members.length === 0 ? (
        <Empty>
          {query
            ? 'No member here by that name or street.'
            : status === 'active'
              ? 'No members yet. A membership is sold on a visit’s invoice, from the iPad or the office.'
              : status === 'at_risk'
                ? 'No renewal is at risk.'
                : 'No memberships here.'}
        </Empty>
      ) : (
        <>
          {/* Blueprint's figures over the whole list, every page (DESIGN.md §3) */}
          {bp && <MemberFacts page={q.first} />}
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label="Members">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Plan</th>
                  <th>Since</th>
                  <th>Renews</th>
                  <th>Visits</th>
                  <th>Next visit</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {q.members.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <button
                        type="button"
                        className="row-btn member-open"
                        onClick={() => setOpen(m)}
                        aria-label={`${m.customerName}’s ${m.planName} membership`}
                      >
                        <span className="who-col">
                          <Who m={m} />
                        </span>
                      </button>
                    </td>
                    <td>{m.planName}</td>
                    <td>{sinceText(m.startsOn)}</td>
                    <td>
                      {dayText(m.endsOn)}
                      {m.status === 'active' && (
                        <span className="muted small block">{whenText(m.endsOn, today)}</span>
                      )}
                      {m.renewal && m.renewal.state !== 'scheduled' && (
                        <span className="block">
                          <RenewalChip m={m} />
                        </span>
                      )}
                      {m.billing?.atRisk && (
                        <span className="block">
                          <BillChip m={m} />
                        </span>
                      )}
                    </td>
                    <td>{visitsText(m.visitsUsed, m.visitsTotal)}</td>
                    <td>{m.nextDueOn ? dayText(m.nextDueOn) : <span className="muted">None</span>}</td>
                    <td>
                      <StatusChip m={m} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <MoreMembers list={q} />
        </>
      )}
      {open && (
        <MemberDetail
          member={open}
          today={today}
          // choosing the card needs the sale's grant and the customer's cards (customers.edit)
          canChooseCard={holdsAny(held, MEMBERSHIP_SELL_PERMS) && keepsCards(held)}
          canRenew={holdsAny(held, MEMBERSHIP_SELL_PERMS)}
          canManage={holdsAny(held, STATUS_PERMS)}
          onChanged={() => void q.reload()}
          onClose={() => setOpen(null)}
        />
      )}
    </Panel>
  );
}

/** One member (memberships.get): its term, the visits used and the next one due, and its revenue. */
function MemberDetail({
  member: m,
  today,
  canChooseCard,
  canRenew,
  canManage = false,
  onChanged,
  onClose,
}: {
  member: Member;
  today: string;
  canChooseCard: boolean;
  /** memberships.sell: turn auto-renew on (with consent) or off. */
  canRenew: boolean;
  /** memberships.manage: suspend, resume and cancel it (BL-056). */
  canManage?: boolean;
  /** After its status changed, for the list it opened from. */
  onChanged?: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [d, setD] = useState<MembershipDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<{ message: string; customerId?: string; creditCents?: number } | null>(
    null,
  );
  const [version, setVersion] = useState(0);
  // ServiceTitan's while the company mirrors it: suspended, cancelled and renewed there (history fix 2)
  const theirs = useMirror() && !!d?.imported;
  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
    client
      .call<MembershipDetail>('memberships.get', { id: m.id })
      .then(setD)
      .catch((e: unknown) => setError(errorText(e, 'Could not load this membership.')));
  }, [m.id, version]);
  return (
    <dialog ref={ref} className="sheet ask member-detail" aria-labelledby="member-title" onClose={onClose}>
      <div className="row between">
        <h3 id="member-title">
          {m.customerName}: {m.planName}
        </h3>
        <button
          type="button"
          className="btn quiet icon"
          aria-label="Close"
          onClick={() => ref.current?.close()}
        >
          ×
        </button>
      </div>
      <p className="muted">{m.address}</p>
      {error && <p className="error">{error}</p>}
      {!d && !error && <Loading error={null} what="the membership" />}
      {d && (
        <>
          <div className="row">
            <StatusChip m={{ status: d.status as Member['status'], cancelsOn: d.cancelsOn ?? null }} />
            {d.status === 'active' && !d.cancelsOn && (
              <span className="muted small">Renews {whenText(d.endsOn, today)}</span>
            )}
            {theirs && <span className="muted small">From ServiceTitan</span>}
          </div>
          <StatusSaid said={said} />
          {/* why it is paused or ends (BL-056) */}
          {d.status === 'suspended' && (
            <p className="small">
              Suspended since {dayText(d.suspendedOn!)}
              {d.suspendReason ? `: ${d.suspendReason}` : ''}. No renewal and no member prices until it is
              resumed. <span className="muted">{suspendNote(d.endsOn, dayText)}</span>
            </p>
          )}
          {(d.cancelsOn || d.cancelledOn) && (
            <p className="small">
              {d.cancelsOn ? `Cancels on ${dayText(d.cancelsOn)}` : `Cancelled on ${dayText(d.cancelledOn!)}`}
              {d.cancelReason ? `: ${d.cancelReason}` : ''}.
              {d.refundedCents ? (
                <>
                  {' '}
                  <Money cents={d.refundedCents} /> of it was refunded as a credit.
                </>
              ) : null}
            </p>
          )}
          <div className="sell-facts">
            <span className="pebble">
              <span>Term</span>
              <b>{termSpanText(d.startsOn, d.endsOn)}</b>
            </span>
            <span className="pebble">
              <span>Visits</span>
              <b>{visitsText(d.visitsUsed, d.visitsTotal)}</b>
            </span>
            <span className="pebble">
              <span>Next visit due</span>
              <b>{d.nextDueOn ? dayText(d.nextDueOn) : 'None'}</b>
            </span>
          </div>
          <dl className="kv">
            <dt>Paid for the term</dt>
            <dd>
              <Money cents={d.deferredCents} />
            </dd>
            <dt>Earned so far</dt>
            <dd>
              <Money cents={d.recognisedCents} />{' '}
              <span className="muted small">recognised visit by visit</span>
            </dd>
            <dt>Renewal</dt>
            <dd>
              {m.renewal && m.renewal.state !== 'scheduled' ? (
                <RenewalChip m={m} note />
              ) : d.billing ? (
                // billed by instalments: its term isn't charged at once, so it's renewed by hand (BL-009)
                'Renewed by hand when the term ends'
              ) : (
                renewalValue(d.renewal, m.autoRenew)
              )}
              {/* what's left to do for it (F-27): "Auto-renew on: add a card before Nov 5, 2027." */}
              {d.renewal?.notice && <span className="small renewal-notice"> {d.renewal.notice}</span>}
            </dd>
            {/* billed by instalments (BL-009): "Bills $20.00 monthly, next on Dec 3, 2026." */}
            {d.billing && (
              <>
                <dt>Billing</dt>
                <dd>
                  <BillingValue billing={d.billing} />
                </dd>
              </>
            )}
          </dl>
          {canManage && !theirs && d.billing && (d.status === 'active' || d.status === 'suspended') && (
            <BillingActions
              billing={d.billing}
              membershipId={d.id}
              name={`${m.customerName}’s ${m.planName} membership`}
              today={today}
              onChanged={(next, billing) => {
                setSaid(next);
                setD({ ...d, billing });
              }}
            />
          )}
          {/* on with the customer's consent to the terms shown, or off (memberships.setAutoRenew, F-27):
              after the list, so the Renewal value reads on its own */}
          {canRenew && !theirs && d.status === 'active' && (
            <AutoRenew
              detail={d}
              membership={`${m.customerName}’s ${m.planName} membership`}
              onChanged={(next) => setD({ ...d, renewal: next })}
            />
          )}
          {canChooseCard && !theirs && d.renewal?.on && (
            <RenewalCard detail={d} onChanged={(next) => setD({ ...d, renewal: next })} />
          )}
          {/* the customer adds the card (and their yes to the terms) themselves, from a link */}
          {canChooseCard && !theirs && d.status === 'active' && <SendCardLink membershipId={d.id} />}
          {canManage && !theirs && (
            <MemberStatusActions
              key={`${d.status}-${d.cancelsOn ?? ''}`}
              detail={d}
              name={`${m.customerName}’s ${m.planName} membership`}
              today={today}
              onChanged={(next) => {
                setSaid(next);
                setVersion((v) => v + 1);
                onChanged?.();
              }}
            />
          )}
        </>
      )}
      <div className="row end">
        <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
          Close
        </button>
      </div>
    </dialog>
  );
}

/**
 * "Send card link" (memberships.sendCardLink, BL-009 phase 4): texts or emails the customer a link to
 * add the card their membership renews to, agreeing to the renewal terms there if they haven't.
 */
function SendCardLink({ membershipId }: { membershipId: string }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);
  const send = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await client.call<{ sent: boolean; reason: string | null }>(
        'memberships.sendCardLink',
        { membershipId },
        { idempotencyKey: newId() },
      );
      setSaid(
        r.sent
          ? { ok: true, text: 'Sent. The link works for 30 days, or until they save a card with it.' }
          : { ok: false, text: r.reason ?? 'The link wasn’t sent.' },
      );
    } catch (e) {
      setSaid({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="renewal-card">
      <button type="button" className="btn quiet" disabled={busy} onClick={() => void send()}>
        {busy ? 'Sending…' : 'Send card link'}
      </button>
      <span className="muted small"> They add the card it renews to themselves; nothing is charged now.</span>
      {said && (
        <p className={said.ok ? 'small' : 'error'} role={said.ok ? 'status' : 'alert'}>
          {said.text}
        </p>
      )}
    </div>
  );
}

/** "Renews to [card]" (memberships.setRenewalCard): one of the customer's unexpired cards on file. */
function RenewalCard({
  detail: d,
  onChanged,
}: {
  detail: MembershipDetail;
  onChanged: (renewal: NonNullable<MembershipDetail['renewal']>) => void;
}) {
  const [cards, setCards] = useState<Card[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<{ cards: Card[] }>('cards.list', { customerId: d.customerId })
      .then((r) => setCards(r.cards.filter((c) => !c.expired)))
      .catch((e: unknown) => setError(errorText(e, 'Could not read their cards on file.')));
  }, [d.customerId]);
  const current = d.renewal?.card?.id ?? '';
  const choose = async (cardId: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ renewal: NonNullable<MembershipDetail['renewal']> }>(
        'memberships.setRenewalCard',
        { membershipId: d.id, cardId },
      );
      onChanged(r.renewal);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!cards) return error ? <p className="error">{error}</p> : null;
  return (
    <div className="renewal-card">
      {cards.length ? (
        <label>
          Renews to
          <select
            className="field"
            value={current}
            disabled={busy}
            onChange={(e) => e.target.value && void choose(e.target.value)}
          >
            {!current && <option value="">Choose a card on file</option>}
            {cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="muted small">No card on their file yet: save one on their Money tab.</p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface Due {
  recurringServiceId: string;
  membershipId: string;
  customerName: string;
  address: string;
  membershipName: string;
  jobTypeName: string;
  dueOn: string;
  visitsLeft: number;
}

function VisitsDue({ today, held, toast, onBook }: Props & { onBook: () => void }) {
  const q = useQuery<{ due: Due[] }>('recurringServices.due', { asOf: addDaysTo(today, DUE_DAYS) });
  // a visit the customer won't take: skipped, its share recognised (memberships.skipVisit, BL-056)
  const canSkip = holdsAny(held, STATUS_PERMS);
  // To book is the bookers' place; anyone else (the bookkeeper) sees the list without the way in.
  return (
    <Panel
      title="Coming due"
      aside={
        holdsAny(held, BOOKER_PERMS) && (
          <button className="btn" onClick={onBook}>
            Book them in To book
          </button>
        )
      }
    >
      {!q.data ? (
        <Loading
          error={q.error}
          what="the visits due"
          kind="table"
          cols={['Due', 'Member', 'Visit']}
          retry={() => void q.reload()}
        />
      ) : q.data.due.length === 0 ? (
        <Empty>No included visit is due in the next {DUE_DAYS} days.</Empty>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label="Visits coming due">
            <thead>
              <tr>
                <th>Due</th>
                <th>Member</th>
                <th>Plan</th>
                <th>Visit</th>
                <th className="num">Left this term</th>
                {canSkip && <th />}
              </tr>
            </thead>
            <tbody>
              {q.data.due.map((d) => (
                <tr key={d.recurringServiceId}>
                  <td>
                    {dayText(d.dueOn)}
                    <span className={`small block${d.dueOn < today ? ' bad-ink' : ' muted'}`}>
                      {whenText(d.dueOn, today)}
                    </span>
                  </td>
                  <td>
                    <Who m={d} />
                  </td>
                  <td>{d.membershipName}</td>
                  <td>{d.jobTypeName}</td>
                  <td className="num">{d.visitsLeft}</td>
                  {canSkip && (
                    <td>
                      <SkipVisit
                        recurringServiceId={d.recurringServiceId}
                        what={`${d.customerName}’s ${d.jobTypeName.toLowerCase()} due ${dayText(d.dueOn)}`}
                        onDone={(said) => {
                          toast(said);
                          void q.reload();
                        }}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** Renewals that didn't go through (BL-009): who to call, what happened, and their sheet. */
function AtRisk({ today, held, focus }: Props) {
  const q = useMemberPages(AT_RISK_INPUT);
  const [open, setOpen] = useState<Member | null>(null);
  useReadTo(q, focus);
  useDocFocus(q.members?.some((m) => m.id === focus) ? focus : null, !!q.members);
  return (
    <Panel title="At risk">
      {!q.members ? (
        <Loading
          error={q.error}
          what="the renewals at risk"
          kind="table"
          cols={['Member', 'Plan', 'Term ends']}
          retry={() => void q.reload()}
        />
      ) : q.members.length === 0 ? (
        <Empty>No renewal is at risk.</Empty>
      ) : (
        <>
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label="Renewals at risk">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Plan</th>
                  <th>Term ends</th>
                  <th>What happened</th>
                </tr>
              </thead>
              <tbody>
                {q.members.map((m) => (
                  <tr key={m.id} data-doc={m.id}>
                    <td>
                      <button
                        type="button"
                        className="row-btn member-open"
                        onClick={() => setOpen(m)}
                        aria-label={`${m.customerName}’s ${m.planName} membership`}
                      >
                        <span className="who-col">
                          <Who m={m} />
                        </span>
                      </button>
                    </td>
                    <td>{m.planName}</td>
                    <td>{dayText(m.endsOn)}</td>
                    <td>
                      <RenewalChip m={m} note />
                      {m.billing?.atRisk && (
                        <span className="block">
                          <BillChip m={m} note />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <MoreMembers list={q} />
        </>
      )}
      {open && (
        <MemberDetail
          member={open}
          today={today}
          canChooseCard={holdsAny(held, MEMBERSHIP_SELL_PERMS) && keepsCards(held)}
          canRenew={holdsAny(held, MEMBERSHIP_SELL_PERMS)}
          // a bill at risk: the owner or manager moves its next bill or pauses billing from here (BL-009)
          canManage={holdsAny(held, STATUS_PERMS)}
          onChanged={() => void q.reload()}
          onClose={() => setOpen(null)}
        />
      )}
    </Panel>
  );
}

function Renewals({ today, toast, held, focus }: Props) {
  // by end date across every page, the soonest first (list caps M6)
  const q = useMemberPages(renewingInput(today));
  // a declined, no-card or expiring-card notice is about one renewing soon (an at-risk one is above)
  useReadTo(q, focus);
  useDocFocus(q.members?.some((m) => m.id === focus) ? focus : null, !!q.members);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  // memberships.expire: the plans past their end date end, and their unused revenue is recognised.
  const expire = async () => {
    setBusy(true);
    setWhy(null);
    try {
      const r = await client.call<{ expired: number }>('memberships.expire', { asOf: today });
      toast(expiredText(r.expired));
      q.reload();
    } catch (e) {
      setWhy(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel
      title="Renewing soon"
      aside={
        holdsAny(held, EXPIRE_PERMS) ? (
          <SaveButton
            busy={busy}
            busyLabel="Ending…"
            error={why}
            className="btn"
            onClick={() => void expire()}
          >
            End the plans that are over
          </SaveButton>
        ) : undefined
      }
    >
      {!q.members ? (
        <Loading
          error={q.error}
          what="the renewals"
          kind="table"
          cols={['Renews', 'Member', 'Plan']}
          retry={() => void q.reload()}
        />
      ) : q.members.length === 0 ? (
        <Empty>No plan ends in the next {RENEWAL_DAYS} days.</Empty>
      ) : (
        <>
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label="Renewals">
              <thead>
                <tr>
                  <th>Renews</th>
                  <th>Member</th>
                  <th>Plan</th>
                  <th>Renewal</th>
                  <th>Unused visits</th>
                </tr>
              </thead>
              <tbody>
                {q.members.map((m) => (
                  <tr key={m.id} data-doc={m.id}>
                    <td>
                      {dayText(m.endsOn)}
                      <span className="muted small block">{whenText(m.endsOn, today)}</span>
                    </td>
                    <td>
                      <Who m={m} />
                    </td>
                    <td>{m.planName}</td>
                    <td>
                      {m.renewal ? (
                        <RenewalChip m={m} note />
                      ) : m.autoRenew ? (
                        'Renews by itself'
                      ) : (
                        'Ask them to renew'
                      )}
                    </td>
                    <td>
                      {m.visitsTotal - m.visitsUsed > 0 ? `${m.visitsTotal - m.visitsUsed} to book` : 'None'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <MoreMembers list={q} />
        </>
      )}
    </Panel>
  );
}

type ServiceItem = { id: string; name: string; priceCents: number };

/** Every service in the price book, a page at a time: past the 200 a page gives (list caps M3). */
function useServices() {
  const [items, setItems] = useState<ServiceItem[] | null>(null);
  useEffect(() => {
    let current = true;
    everyPage<ServiceItem, { name: string; id: string }>((after) =>
      client.call('pricebook.listItems', { type: 'service', limit: 200, ...(after ? { after } : {}) }),
    )
      .then((got) => current && setItems(got))
      // the picker stays empty, as it did when the one call failed; the plan can't be added without one
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, []);
  return items;
}

function NewPlan({ act, onDone }: { act: ReturnType<typeof useAct>; onDone: () => void }) {
  const items = useServices();
  const types = useQuery<{ jobTypes: { id: string; name: string }[] }>('jobTypes.list', {});
  const [name, setName] = useState('');
  const [itemId, setItem] = useState('');
  const [term, setTerm] = useState('12');
  const [visits, setVisits] = useState('1');
  const [jobType, setJobType] = useState('');
  const [discount, setDiscount] = useState('0');
  const [cancelRefund, setCancelRefund] = useState<CancelRefund>('optional');
  const visitsN = Number(visits);
  const ready =
    name.trim() &&
    itemId &&
    Number.isInteger(Number(term)) &&
    Number(term) >= 1 &&
    Number.isInteger(visitsN) &&
    visitsN >= 0 &&
    (visitsN === 0 || jobType) &&
    /^\d{1,3}(\.\d{1,2})?$/.test(discount);
  return (
    <form
      className="fgrid"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || act.busy) return;
        void act
          .run(
            'membershipTypes.create',
            {
              name: name.trim(),
              pricebookItemId: itemId,
              termMonths: Number(term),
              visitsPerTerm: visitsN,
              ...(visitsN > 0 ? { visitJobTypeId: jobType } : {}),
              discountPct: discount,
              cancelRefund,
            },
            `${name.trim()} added.`,
          )
          .then((ok) => {
            if (ok) onDone();
          });
      }}
    >
      <label className="wide">
        <span className="flabel">Plan name</span>
        <input
          className="field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Maintenance plan"
        />
      </label>
      <label className="wide">
        <span className="flabel">Sold as (its price and tax)</span>
        <select className="field" value={itemId} onChange={(e) => setItem(e.target.value)}>
          <option value="">Pick a pricebook item…</option>
          {(items ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} · {(i.priceCents / 100).toFixed(2)}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">Term (months)</span>
        <input className="field" inputMode="numeric" value={term} onChange={(e) => setTerm(e.target.value)} />
      </label>
      <label>
        <span className="flabel">Visits included</span>
        <input
          className="field"
          inputMode="numeric"
          value={visits}
          onChange={(e) => setVisits(e.target.value)}
        />
      </label>
      {visitsN > 0 && (
        <label>
          <span className="flabel">Visit type</span>
          <select className="field" value={jobType} onChange={(e) => setJobType(e.target.value)}>
            <option value="">Pick a job type…</option>
            {(types.data?.jobTypes ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        <span className="flabel">Member discount (%)</span>
        <input
          className="field"
          inputMode="decimal"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
        />
      </label>
      <label className="wide">
        <span className="flabel">At a cancel</span>
        <select
          className="field"
          value={cancelRefund}
          onChange={(e) => setCancelRefund(e.target.value as CancelRefund)}
        >
          {(Object.keys(CANCEL_REFUND_WORDS) as CancelRefund[]).map((k) => (
            <option key={k} value={k}>
              {CANCEL_REFUND_WORDS[k]}
            </option>
          ))}
        </select>
        <span className="muted small block">What a member paid and hasn’t used yet, when they cancel.</span>
      </label>
      <div className="row-actions">
        <button className="btn primary" type="submit" disabled={!ready || act.busy}>
          {act.busy ? 'Adding…' : 'Add the plan'}
        </button>
      </div>
    </form>
  );
}

function Plans({ toast, held }: Props) {
  const q = useQuery<{ plans: EditablePlan[] }>('membershipTypes.list', {});
  const act = useAct(toast, async () => {
    await q.reload();
  });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<EditablePlan | null>(null);
  const mayAdd = holdsAny(held, PLAN_PERMS);
  // Blueprint: Add a plan is the section's one primary
  const bp = useDesign() === 'blueprint';
  return (
    <>
      <Panel
        title="Plans"
        aside={
          mayAdd && !adding ? (
            <button className={bp ? 'btn primary' : 'btn'} onClick={() => setAdding(true)}>
              Add a plan
            </button>
          ) : undefined
        }
      >
        {!q.data ? (
          <Loading
            error={q.error}
            what="the plans"
            kind="table"
            cols={['Plan', 'Price', 'Term']}
            retry={() => void q.reload()}
          />
        ) : q.data.plans.length === 0 ? (
          <Empty>No plans yet.{mayAdd ? ' Add the first one.' : ' The owner or a manager adds them.'}</Empty>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table plans-table" aria-label="Plans">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th className="num">Price</th>
                  {/* on a phone, said under the plan's name instead, so Change stays in view */}
                  <th className="wide-col">Term</th>
                  <th className="wide-col">Includes</th>
                  <th className="wide-col">Discount</th>
                  <th className="num wide-col">Members</th>
                  {mayAdd && <th aria-label="Change" />}
                </tr>
              </thead>
              <tbody>
                {q.data.plans.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <b>{p.name}</b>
                      {!p.active && <span className="chip small-chip">Not sold now</span>}
                      {/* imported without them (ServiceTitan said none): sold once they're set */}
                      {p.bare && <span className="chip small-chip">Set its term, visits and price</span>}
                      <span className="phone-only muted small">
                        {[
                          termText(p.termMonths),
                          includesText(p),
                          discountText(p.discountPct) ?? 'no discount',
                          `${p.activeMembers} ${p.activeMembers === 1 ? 'member' : 'members'}`,
                        ].join(' · ')}
                      </span>
                    </td>
                    <td className="num">
                      {p.priceCents === null ? (
                        <span className="muted">{NOT_SET}</span>
                      ) : (
                        <Money cents={p.priceCents} />
                      )}
                    </td>
                    <td className="wide-col">{termText(p.termMonths)}</td>
                    <td className="wide-col">{includesText(p)}</td>
                    <td className="wide-col">
                      {discountText(p.discountPct) ?? <span className="muted">None</span>}
                    </td>
                    <td className="num wide-col">{p.activeMembers}</td>
                    {mayAdd && (
                      <td>
                        <button
                          type="button"
                          className="btn quiet sm"
                          aria-label={`Change ${p.name}`}
                          onClick={() => setEditing(p)}
                        >
                          Change
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {editing && (
        <Panel
          title={`Change ${editing.name}`}
          aside={
            <button className="btn quiet" onClick={() => setEditing(null)}>
              Cancel
            </button>
          }
        >
          <EditPlan
            key={editing.id}
            plan={editing}
            onClose={() => setEditing(null)}
            onSaved={(said) => {
              setEditing(null);
              toast(said);
              void q.reload();
            }}
          />
        </Panel>
      )}
      {adding && (
        <Panel
          title="A new plan"
          aside={
            <button className="btn quiet" onClick={() => setAdding(false)}>
              Cancel
            </button>
          }
        >
          <NewPlan act={act} onDone={() => setAdding(false)} />
        </Panel>
      )}
    </>
  );
}

function MemberFacts({ page }: { page: MemberPage }) {
  const f = memberFacts(page);
  return (
    <dl className="bp-facts" aria-label="The members in figures">
      <div>
        <dt>Members</dt>
        <dd>{f.members}</dd>
      </div>
      <div>
        <dt>Visits used</dt>
        <dd>
          {f.visitsUsed}
          <small>of {f.visitsTotal} included</small>
        </dd>
      </div>
      <div>
        <dt>Renew in six weeks</dt>
        <dd>{f.renewSoon}</dd>
      </div>
      <div>
        <dt>Renew by themselves</dt>
        <dd>
          {f.autoRenew}
          <small>of {f.members}</small>
        </dd>
      </div>
    </dl>
  );
}

type ScreenProps = {
  nav: NavProps;
  held: readonly string[];
  /** A membership's renewal notification: Renewals, on its row. */
  open?: { part: 'renewals'; at: number; focus?: string };
};

/** Customers › Memberships: Blueprint's on ListPage (MembershipsPage.tsx), Riverstone's own. */
export function MembershipsScreen(props: ScreenProps) {
  return useDesign() === 'blueprint' ? <MembershipsPage {...props} /> : <RiverstoneMemberships {...props} />;
}

function RiverstoneMemberships({ nav, held, open }: ScreenProps) {
  const [part, setPart] = useState<Part>(open?.part ?? 'members');
  useEffect(() => {
    if (open) setPart(open.part);
  }, [open?.part, open?.at]);
  const [toast, setToast] = useToast();
  const here = PARTS.find((p) => p.part === part)!;
  // Only those who book are sent to To book (the button shows to them only, cb38805)
  const books = holdsAny(held, BOOKER_PERMS);
  // While the company mirrors ServiceTitan its memberships, plans and renewals are ServiceTitan's
  // (st-mirror §2): every part reads them, and none offers a change; the page says so once (BL-088)
  const mirroring = useMirror();
  const props: Props = {
    today: todayIn(nav.timeZone),
    toast: setToast,
    held: mirroring ? [] : held,
    ...(part === 'renewals' && open?.focus ? { focus: open.focus } : {}),
  };
  // how many renewals are at risk, beside Renewals in the parts (BL-009)
  // the whole list's count, not a page's: one row is enough to read it
  const risk = useQuery<MemberPage>('memberships.list', { ...AT_RISK_INPUT, limit: 1 });
  const riskWords = atRiskCount(risk.data?.total ?? 0);
  const bp = useDesign() === 'blueprint';
  return (
    <div className={bp ? 'app books memberships bp-place' : 'app books memberships'}>
      {/* Blueprint's crumb, Customers › Memberships, is the Header's */}
      <Header nav={nav} title={here.title} lede={part === 'due' && !books ? DUE_LEDE_READ : here.lede} />
      <div className="books-body">
        <nav className="books-parts" aria-label="Memberships">
          {PARTS.map((p) => (
            <button
              key={p.part}
              className={`btn quiet${part === p.part ? ' on' : ''}`}
              aria-current={part === p.part ? 'page' : undefined}
              onClick={() => setPart(p.part)}
            >
              {p.label}
              {p.part === 'renewals' && riskWords && (
                <span className="count" aria-label={riskWords}>
                  {risk.data!.total}
                </span>
              )}
            </button>
          ))}
        </nav>
        <main className="books-main">
          <div className="books-page">
            {mirroring && <p className="muted small">{MIRROR_LINE}</p>}
            {part === 'members' && <Members {...props} />}
            {part === 'due' && <VisitsDue {...props} onBook={() => nav.onView('tobook')} />}
            {part === 'renewals' && (
              <>
                {/* only while there is someone to call (the count beside Renewals says how many) */}
                {riskWords && <AtRisk {...props} />}
                <Renewals {...props} />
              </>
            )}
            {/* Blueprint: the plans are Setup's now (the setup audit); for a release, a way there */}
            {part === 'plans' && !bp && <Plans {...props} />}
            {part === 'plans' && bp && (
              <Moved what="Membership plans" to="Setup › Membership plans" href="#/settings/plans" />
            )}
          </div>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

/** Setup › Membership plans › Plans (Blueprint, the setup audit): the plans, as Memberships had them. */
export function PlansPage({ held, timeZone }: { held: readonly string[]; timeZone: string }) {
  const [toast, setToast] = useToast();
  // ServiceTitan's while the company mirrors it (BL-088): read, never changed
  const mirroring = useMirror();
  // Memberships' own wrappers, so its Blueprint styles hold here too (history/bp-customers.css)
  return (
    <div className="books memberships bp-place">
      {mirroring && <p className="muted small">{MIRROR_LINE}</p>}
      <div className="books-body">
        <Plans today={todayIn(timeZone)} toast={setToast} held={mirroring ? [] : held} />
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
